import { css, Global } from "@emotion/react";
import { LocalDateTime } from "@js-joda/core";
import React from "react";
import { CommCenterTicketCreateParams } from "../../activities/AppNavigator";
import { Page } from "../../activities/AppPage";
import { API } from "../../API";
import { isReminderRelevantForVisit } from "../../clockInOutUtils";
import useClockedInStatus from "../../hooks/useClockedInStatus";
import useComplianceIssuesModal from "../../hooks/useComplianceIssuesModal";
import useMixin from "../../hooks/useMixin";
import usePatientsState from "../../hooks/usePatientsState";
import { isLoading, Loading } from "../../Loading";
import {
  CaregiverStatus,
  ClockedInStatusDetails,
  ClockInOutCallType,
  OnboardingVisitId,
  PatientTaskInstanceId,
  VisitBroadcastId,
  VisitInstanceClockinClockoutRequestParams,
  VisitInstanceDetails,
  VisitInstanceId,
} from "../../messages";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import { AppCaregiver } from "../../system/AppState/AppCaregiverState";
import { AppScheduledVisitsState } from "../../system/AppState/AppScheduledVisitsState";
import { Visit } from "../../system/AppState/AppVisitsState";
import { ChatSystemStrategies } from "../../system/AppState/ChatState";
import { translate } from "../../translate";
import { usePendingPatientTasksBottomSheet } from "../../usePatientTasksBottomSheet";
import { isNonEmptyArray } from "../../utils/arrayUtils";
import { flmap } from "../../utils/fmap";
import { CurrencyCircleDollarIcon, GiftFillIcon } from "../../utils/icones";
import { getVisitStatus } from "../AvailableVisitsAndTasksList";
import { DocumentsWithRequiredFixesBottomSheet } from "../bottomsheets/DocumentsWithRequiredFixesBottomSheet";
import ChatActivity from "../Chat/ChatMainScreen/ChatActivity";
import { useFinishOnboarding } from "../FinishOnboardingSection";
import InstaPayActivity from "../InstaPay/InstaPayActivity";
import ActiveVisitBottomSheetsSubActivity, {
  useActiveVisitBottomSheetsSubActivity,
} from "../Main/ActiveVisitBottomSheetsSubActivity";
import useTotalNewChatMessagesCount from "../Main/hooks/useTotalNewChatMessagesCount";
import PatientsActivity from "../Patients/PatientsActivity";
import { PendingPatientTasksBottomSheet } from "../PendingPatientTasksBottomSheet/PendingPatientTasksBottomSheet";
import ScheduledVisitsActivity from "../ScheduledVisits/ScheduledVisitsActivity";
import useContactSupport from "../useContactSupport";
import { useMedflytAgencyId } from "../withMedflytAgencyId";
import ConnectionStatus from "./components/ConnectionStatus";
import { MainBannerProps } from "./components/MainBanner";
import MainOverlays from "./components/MainOverlays";
import useCaregiverMedflytAgencyStatus from "./hooks/useCaregiverMedflytAgencyStatus";
import MainV2Screen from "./MainV2Screen";

export type MainV2ActivityProps = {
  api: API;
  appState: AppState;
  caregiver: AppCaregiver;
  isPromoOpen: boolean;
  isRouteStackEmpty: boolean;
  hideFinishOnboardingModal: boolean;
  chatSystemStrategies: ChatSystemStrategies;
  onRequestNavigate: (page: Page) => void;
  onClickAvailableVisit: (visitBroadcastId: VisitBroadcastId) => void;
  onClickAvailableTask: (taskInstanceId: PatientTaskInstanceId) => void;
  onClickAssignedTask: (taskInstanceId: PatientTaskInstanceId) => void;
  onClickOnboardingVisit: (onboardingVisitId: OnboardingVisitId) => void;
  onClickNewChatWithMedflyt: (params: CommCenterTicketCreateParams) => void;
  onClickConfirmAssignment: (visitId: VisitBroadcastId, isReviewOnly: boolean) => void;
  onClock: (
    visitInstanceId: VisitInstanceId,
    clockInOutCallType: ClockInOutCallType,
    visitInstanceClockinClockoutRequestParams: VisitInstanceClockinClockoutRequestParams
  ) => Promise<boolean>;
};

type ActiveTab =
  | {
      tab: "my-visits";
      params?: {
        isApplyOnlyTasksWithRequiredFixesFilter?: boolean;
      };
    }
  | {
      tab: "explore" | "instapay" | "patients" | "chat";
    };

export default function MainV2Activity(props: MainV2ActivityProps) {
  const [activeTab, setActiveTab] = React.useState<ActiveTab>({
    tab: "explore",
  });

  const mixin = useMixin(props.appState);
  const patientsState = usePatientsState(props.appState);

  const { caregiverStatusInMedflyt } = useCaregiverMedflytAgencyStatus({
    appAgenciesState: props.appState.appAgenciesState,
  });

  const complianceIssuesModal = useComplianceIssuesModal({
    api: props.api,
    appState: props.appState,
    caregiverStatusInMedflyt: caregiverStatusInMedflyt,
    onRequestNavigate: props.onRequestNavigate,
  });

  const onboardingStatus = useFinishOnboarding({
    getApplicationBanner: () => props.api.getApplicationBanner(),
  });

  const notificationsBadge = mixin.notificationsState
    .getNotificationsDB()
    .getNumUnviewedNotifications();

  const handleCloseView = () => {
    setActiveTab({
      tab: "explore",
    });
  };

  const handleClickVisit = (visit: Visit) => {
    switch (visit.type) {
      case "Available": {
        const visitStatus = getVisitStatus(visit);

        if (visitStatus === "PENDING_CONFIRMATION") {
          return props.onClickConfirmAssignment(visit.id, false);
        }

        return props.onClickAvailableVisit(visit.id);
      }
      case "Onboarding": {
        return props.onClickOnboardingVisit(visit.id);
      }
    }
  };

  const handleClickFixDocuments = (taskInstanceId: PatientTaskInstanceId | null) => {
    if (taskInstanceId) {
      props.onRequestNavigate({
        type: "PatientTaskInstance",
        patientTaskInstanceId: taskInstanceId,
        initialScheduleDateTimePickerOpen: false,
      });
    } else {
      setActiveTab({
        tab: "my-visits",
        params: {
          isApplyOnlyTasksWithRequiredFixesFilter: true,
        },
      });
    }
  };

  const totalNewChats = useTotalNewChatMessagesCount({
    api: props.api,
    appState: props.appState,
    appChatState: mixin.appChatState,
    chatSystemStrategies: props.chatSystemStrategies,
    isNewChatScreen: true,
  });

  const contactSupport = useContactSupport({
    api: props.api,
    appState: props.appState,
    onClickNewChatWithMedflyt: props.onClickNewChatWithMedflyt,
    onRequestNavigate: props.onRequestNavigate,
  });

  const clockedInStatus = useClockedInStatus(props.appState);

  const activeVisitBottomSheetsProps = useActiveVisitBottomSheetsSubActivity({
    api: props.api,
    appState: props.appState,
    contactSupport: contactSupport,
  });

  const clockActionBannerProps = getClockBannerProps({
    scheduledVisits: mixin.scheduledVisits,
    status: flmap(clockedInStatus, (x) => x.status),
    onClock: (p) => {
      switch (p.type) {
        case "IN":
          return p.visits.length === 1
            ? activeVisitBottomSheetsProps.setActiveVisitById(p.visits[0].id)
            : activeVisitBottomSheetsProps.visitReminderDisclosure.actions.open();
        case "OUT":
          return activeVisitBottomSheetsProps.openVisitBottomSheet({
            type: "visit",
            visit: p.visit,
          });
      }
    },
  });

  const activeVisitBottomSheets = (
    <ActiveVisitBottomSheetsSubActivity
      api={props.api}
      appState={props.appState}
      contactSupport={contactSupport}
      hookProps={activeVisitBottomSheetsProps}
    />
  );

  const { bottomSheetProps: pendingPatientTasksBottomSheetProps } =
    usePendingPatientTasksBottomSheet({
      tasksState: mixin.appTasksState,
      onClickReviewTasks: () => {
        setActiveTab({
          tab: "my-visits",
        });
      },
    });

  const pendingPatientTasksBottomSheet = (
    <PendingPatientTasksBottomSheet {...pendingPatientTasksBottomSheetProps} />
  );

  const payrollView = (
    <InstaPayActivity
      api={props.api}
      appState={props.appState}
      onClickGoBack={handleCloseView}
      onRequestGoToMain={handleCloseView}
      onRequestNavigate={props.onRequestNavigate}
    />
  );

  const patientsView = (
    <PatientsActivity
      appState={props.appState}
      onClickGoBack={handleCloseView}
      onRequestNavigate={props.onRequestNavigate}
      onRequestAvailableVisits={handleCloseView}
    />
  );

  const chatView = (
    <ChatActivity
      api={props.api}
      caregiver={props.caregiver}
      onRequestNavigate={props.onRequestNavigate}
      onRequestGoBack={handleCloseView}
    />
  );

  const visitsView = (
    <ScheduledVisitsActivity
      scheduledVisitsParams={{
        isApplyOnlyTasksWithRequiredFixesFilter:
          activeTab.tab === "my-visits" &&
          activeTab.params?.isApplyOnlyTasksWithRequiredFixesFilter,
      }}
      api={props.api}
      appState={props.appState}
      onClock={props.onClock}
      onClickStartNow={activeVisitBottomSheetsProps.openVisitBottomSheet}
      onRequestNavigate={props.onRequestNavigate}
      onClickNewChatWithMedflyt={props.onClickNewChatWithMedflyt}
      onClickGoBack={handleCloseView}
    />
  );

  const activeView = {
    instapay: payrollView,
    "my-visits": visitsView,
    patients: patientsView,
    chat: chatView,
    explore: null,
  }[activeTab.tab];

  const inactiveAccountBannerStatus =
    getInactiveAccountBannerStatusByCaregiverStatus(caregiverStatusInMedflyt);

  const medflytAgency = useMedflytAgencyId();

  const banners = React.useMemo((): MainBannerProps[] => {
    const banners: MainBannerProps[] = [];

    if (clockActionBannerProps !== null) {
      banners.push({
        id: "clock-action",
        theme: "dark-slate",
        title: clockActionBannerProps.text,
        trailing: { type: "button", label: clockActionBannerProps.button },
        onClick: clockActionBannerProps.onClick,
      });
    }

    if (onboardingStatus.type === "regular") {
      banners.push({
        id: "finish-onboarding",
        theme: "branded-gradient",
        title: translate(STRINGS.FINISH_ONBOARDING_AND_GET_DOLLARS),
        leading: <GiftFillIcon fontSize={24} />,
        trailing: { type: "chevron" },
        onClick: () => props.onRequestNavigate.call(null, { type: "OnboardingActivity" }),
      });
    }

    if (onboardingStatus.type === "onboarding2") {
      banners.push({
        id: "finish-onboarding",
        theme: "branded-gradient",
        title: translate(STRINGS.COMPLETE_ALL_DOCUMENTS_BEFORE_PAYROLL),
        leading: <CurrencyCircleDollarIcon fontSize={24} />,
        trailing: { type: "chevron" },
        onClick: () => props.onRequestNavigate.call(null, { type: "OnboardingActivity" }),
      });
    }

    if (
      complianceIssuesModal.hasIssues &&
      (!onboardingStatus.show || onboardingStatus.type === "onboarding2")
    ) {
      banners.push({
        id: "compliance-issues",
        theme: "fatal",
        title:
          complianceIssuesModal.complianceBannerDetails.message ??
          complianceIssuesModal.complianceBannerDetails.title,
        trailing: { type: "chevron" },
        onClick: () => props.onRequestNavigate.call(null, { type: "MyMedflytCompliance" }),
      });
    }

    if (inactiveAccountBannerStatus.show && !isLoading(medflytAgency)) {
      banners.push({
        id: "inactive-account",
        theme: "warning",
        title:
          inactiveAccountBannerStatus.type === "on-leave"
            ? translate(STRINGS.YOUR_ACCOUNT_IS_CURRENTLY_ON_LEAVE)
            : translate(STRINGS.YOUR_ACCOUNT_IS_CURRENTLY_ON_HOLD),
        description: translate(STRINGS.PLEASE_SEND_US_A_MESSAGE_TO_REINSTATE_IT),
        trailing: { type: "chevron" },
        onClick: async () => {
          const { ticket } = await props.api.createCommCenterTicket({
            source: "MOBILE_CHAT",
            agencyId: medflytAgency,
          });

          props.onRequestNavigate.call(null, { type: "ChatRoom", ticketId: ticket.id });
        },
      });
    }

    return banners;
  }, [
    clockActionBannerProps,
    complianceIssuesModal.complianceBannerDetails.message,
    complianceIssuesModal.complianceBannerDetails.title,
    complianceIssuesModal.hasIssues,
    inactiveAccountBannerStatus.show,
    inactiveAccountBannerStatus.type,
    medflytAgency,
    onboardingStatus.show,
    onboardingStatus.type,
    props.api,
    props.onRequestNavigate,
  ]);

  return (
    <MainV2Screen
      banners={banners}
      activeTab={activeTab.tab}
      activeView={activeView}
      caregiver={props.caregiver}
      notificationsBadge={notificationsBadge}
      visits={flmap(mixin.appVisitsState, (x) => x.visits)}
      tasks={flmap(mixin.appTaskBroadcastsState, (x) => x.tasks)}
      assignedTasks={flmap(mixin.appTasksState, (x) => x.tasks)}
      totalNewChats={totalNewChats}
      caregiverStatusInMedflyt={caregiverStatusInMedflyt}
      connectionStatus={<ConnectionStatus />}
      patientsState={patientsState}
      onClickAdjustments={() =>
        props.onRequestNavigate({
          type: "MedflytCaseSettings",
          showMyStafingPreferences: caregiverStatusInMedflyt !== CaregiverStatus.PENDING,
        })
      }
      onClickNotifications={() => props.onRequestNavigate({ type: "Notifications" })}
      onClickVisit={handleClickVisit}
      onClickTask={props.onClickAvailableTask}
      onClickAssignedTask={props.onClickAssignedTask}
      onClickTab={(tab) => setActiveTab({ tab })}
      onRequestNavigate={props.onRequestNavigate}
      onRequestCloseView={handleCloseView}
      onClickConfirmAssignment={props.onClickConfirmAssignment}
    >
      {activeVisitBottomSheets}
      {pendingPatientTasksBottomSheet}
      {!isLoading(mixin.appTasksState) && (
        <DocumentsWithRequiredFixesBottomSheet
          appTasksState={mixin.appTasksState}
          onClickFixDocuments={handleClickFixDocuments}
          onRequestClose={handleCloseView}
        />
      )}
      {props.isRouteStackEmpty && (
        <Global
          styles={css`
            .connection-overlay {
              display: none !important;
            }
          `}
        />
      )}
      <MainOverlays
        caregiver={props.caregiver}
        mainTab={activeTab.tab}
        isPromoOpen={props.isPromoOpen}
        onboardingStatus={onboardingStatus}
        onRequestNavigate={props.onRequestNavigate}
      />
    </MainV2Screen>
  );
}

function getClockBannerProps(params: {
  scheduledVisits: AppScheduledVisitsState | Loading;
  status: ClockedInStatusDetails | Loading | null;
  onClock: (
    p:
      | { type: "IN"; visits: [VisitInstanceDetails, ...VisitInstanceDetails[]] }
      | { type: "OUT"; visit: VisitInstanceDetails }
  ) => void;
}) {
  const { scheduledVisits, status, onClock } = params;

  if (isLoading(scheduledVisits) || isLoading(status)) {
    return null;
  }

  if (status !== null) {
    const visit = scheduledVisits.scheduledVisitDetails.find(
      (x) => status.visitInstanceId === x.id
    );

    if (visit !== undefined) {
      return {
        text: translate(STRINGS.DONT_FORGET_TO_CLOCK_OUT),
        button: translate(STRINGS.CLOCK_OUT),
        onClick: () => onClock({ type: "OUT", visit }),
      };
    }
  }

  const presentVisits = scheduledVisits.scheduledVisitDetails
    .filter((x) => isReminderRelevantForVisit(x, LocalDateTime.now()) && x.clockInTime === null)
    .toArray();

  if (isNonEmptyArray(presentVisits)) {
    return {
      text: translate(STRINGS.DONT_FORGET_TO_CLOCK_IN),
      button: translate(STRINGS.CLOCK_IN),
      onClick: () => onClock({ type: "IN", visits: presentVisits }),
    };
  }

  return null;
}

function getInactiveAccountBannerStatusByCaregiverStatus(caregiverStatus: CaregiverStatus | null) {
  if (caregiverStatus === CaregiverStatus.ON_HOLD) {
    return {
      show: true,
      type: "on-hold",
    } as const;
  }

  if (caregiverStatus === CaregiverStatus.ON_LEAVE) {
    return {
      show: true,
      type: "on-leave",
    } as const;
  }

  return { show: false } as const;
}
